import { useState, type ReactNode } from "react"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"

export interface BulkDeleteDialogItem {
  id: string | number
}

interface BulkDeleteDialogProps<T extends BulkDeleteDialogItem> {
  isOpen: boolean
  onClose: () => void
  items: T[]
  /** Singular + plural display labels — e.g. `{ singular: "Customer", plural: "Customers" }`. */
  entityLabel: { singular: string; plural: string }
  /** Sentence shown in the warning box explaining what cascades. */
  cascadingWarning: string
  /** Render a single item's primary text (e.g. customer name, invoice number). */
  renderItem: (item: T) => ReactNode
  /** Optional per-item secondary chip/badge (e.g. priority pill). */
  renderItemBadge?: (item: T) => ReactNode
  /** Confirm handler. The dialog closes itself on success; throw to keep it open. */
  onConfirm: (ids: string[]) => Promise<void>
  /** Show item list inline if count is at or below this threshold. Default 5. */
  inlineListThreshold?: number
  /** Optional extra block rendered between the warning box and the footer.
   * Use it to surface deletion-mode selectors (e.g. cascade vs. soft-detach)
   * or other operator choices that affect the cascade. */
  children?: ReactNode
}

export function BulkDeleteDialog<T extends BulkDeleteDialogItem>({
  isOpen,
  onClose,
  items,
  entityLabel,
  cascadingWarning,
  renderItem,
  renderItemBadge,
  onConfirm,
  inlineListThreshold = 5,
  children,
}: BulkDeleteDialogProps<T>) {
  const [isDeleting, setIsDeleting] = useState(false)

  if (!isOpen || items.length === 0) return null

  const count = items.length
  const noun = count > 1 ? entityLabel.plural : entityLabel.singular
  const nounLower = noun.toLowerCase()

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await onConfirm(items.map((i) => i.id.toString()))
      onClose()
    } catch {
      // Parent handles error UI; keep dialog open.
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Dialog open={isOpen} onClose={() => !isDeleting && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              height: 40,
              width: 40,
              flexShrink: 0,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              bgcolor: (t) => alpha(t.palette.error.main, 0.12),
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 20, color: "error.main" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: "text.primary" }}>
              Delete {count} {noun}
            </Typography>
            <Typography sx={{ mt: 0.25, fontSize: 14, color: "text.disabled" }}>
              This action cannot be undone
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: (t) => alpha(t.palette.error.main, 0.3),
            bgcolor: (t) => alpha(t.palette.error.main, 0.06),
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <WarningAmberIcon sx={{ fontSize: 20, mt: 0.25, flexShrink: 0, color: "error.main" }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                component="h4"
                sx={{ mb: 0.5, fontSize: 14, fontWeight: 500, color: "error.dark" }}
              >
                Are you sure you want to delete {count > 1 ? "these" : "this"} {count} {nounLower}?
              </Typography>
              <Typography sx={{ mb: 1, fontSize: 14, color: "error.main" }}>
                {cascadingWarning}
              </Typography>

              {count <= inlineListThreshold && (
                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ mb: 0.5, fontSize: 12, fontWeight: 500, color: "error.main" }}>
                    {noun} to be deleted:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none", display: "grid", gap: 0.5, fontSize: 12, color: "error.main" }}>
                    {items.map((item) => (
                      <Box
                        component="li"
                        key={item.id}
                        sx={{ display: "flex", alignItems: "center", gap: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          • {renderItem(item)}
                        </Box>
                        {renderItemBadge?.(item)}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {children && <Box sx={{ mt: 1.5 }}>{children}</Box>}
      </DialogContent>

      <DialogActions sx={{ gap: 1, px: 3, pb: 2 }}>
        <Button type="button" variant="outlined" color="inherit" onClick={onClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={isDeleting}
          sx={{ minWidth: 120 }}
          startIcon={
            isDeleting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            )
          }
        >
          {isDeleting ? "Deleting..." : `Delete ${count} ${noun}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
